import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../../components/Layout';
import {
  listSupervisors, createSupervisor, updateSupervisor, deleteSupervisor,
} from '../../api/supervisors';
import { listGroups, searchBanks } from '../../api/banks';
import './SupervisorsPage.css';

const emptyForm = { name: '', username: '', password: '', banks: [], groups: [] };

const SupervisorsPage = () => {
  const [supervisors, setSupervisors] = useState([]);
  const [banks, setBanks] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');
  // Groups (en base) du superviseur en cours d'edition, cf. otherGroupName.
  const [ownGroupIds, setOwnGroupIds] = useState(new Set());
  // Tous les groups de la company (2026-10-11) : tableau "Groups" + banques libres.
  const [groups, setGroups] = useState([]);

  // Banks recharges avec les superviseurs : leur group (affiche dans le
  // formulaire) change a chaque enregistrement.
  const load = () => {
    listSupervisors().then(setSupervisors).catch(() => setError('Chargement impossible.'));
    searchBanks('').then(setBanks).catch(() => {});
    listGroups().then(setGroups).catch(() => {});
  };

  const bankLabel = (b) => `${b.seq != null ? `${b.seq} - ` : ''}${b.name}`;
  // Banques sans group (ex. group supprime) : jamais effacees, juste libres —
  // a reaffecter dans un group (formulaire ci-dessous) ou depuis Banque.
  const freeBanks = banks.filter((b) => !b.group);

  useEffect(() => {
    load();
  }, []);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  // ── Banques en extra ────────────────────────────────────────────────────────
  const toggleExtraBank = (bid) => setForm((p) => ({
    ...p,
    banks: p.banks.includes(bid) ? p.banks.filter((x) => x !== bid) : [...p.banks, bid],
  }));

  // ── Groups gérés ────────────────────────────────────────────────────────────
  const addGroup = () => setForm((p) => ({
    ...p,
    groups: [...p.groups, { name: '', address: '', is_print_adresse: false, bank_ids: [] }],
  }));
  const removeGroup = (idx) => setForm((p) => ({
    ...p,
    groups: p.groups.filter((_, i) => i !== idx),
  }));
  const setGroupName = (idx, name) => setForm((p) => ({
    ...p,
    groups: p.groups.map((g, i) => (i === idx ? { ...g, name } : g)),
  }));
  const setGroupAddress = (idx, address) => setForm((p) => ({
    ...p,
    groups: p.groups.map((g, i) => (i === idx ? { ...g, address } : g)),
  }));
  const setGroupPrintAddress = (idx, is_print_adresse) => setForm((p) => ({
    ...p,
    groups: p.groups.map((g, i) => (i === idx ? { ...g, is_print_adresse } : g)),
  }));
  const toggleGroupBank = (idx, bid) => setForm((p) => ({
    ...p,
    // un bank dans un group sort automatiquement des "banques en extra"
    banks: p.banks.filter((x) => x !== bid),
    groups: p.groups.map((g, i) => {
      if (i !== idx) return g;
      const has = g.bank_ids.includes(bid);
      return { ...g, bank_ids: has ? g.bank_ids.filter((x) => x !== bid) : [...g.bank_ids, bid] };
    }),
  }));

  // ids de banks déjà pris par un group du formulaire (indispo en extra / dans les autres groups)
  const groupedBankIds = (exceptIdx) => new Set(
    form.groups.flatMap((g, i) => (i === exceptIdx ? [] : g.bank_ids)),
  );

  // Group ACTUEL (en base) d'un bank s'il appartient a un AUTRE superviseur que
  // celui qu'on edite (2026-10-05 : ces banks s'affichaient comme libres dans
  // "Banques en extra"). Les groups de ce superviseur (meme retires du
  // formulaire, ils seront supprimes a l'enregistrement) ne comptent pas.
  const otherGroupName = (b) => (
    b.group && !ownGroupIds.has(b.group) ? (b.group_name || 'un autre group') : null
  );

  const startEdit = (s) => {
    setEditId(s.id);
    setOwnGroupIds(new Set((s.groups || []).map((g) => g.id)));
    setForm({
      name: s.name,
      username: s.username,
      password: '',
      banks: s.banks || [],
      groups: (s.groups || []).map((g) => ({
        id: g.id, name: g.name, address: g.address || '', is_print_adresse: !!g.is_print_adresse, bank_ids: g.bank_ids || [],
      })),
    });
    setMsg('');
  };
  const cancel = () => { setEditId(null); setForm(emptyForm); setOwnGroupIds(new Set()); };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    setMsg('');
    try {
      const payload = { ...form };
      if (!payload.password) delete payload.password;
      payload.groups = payload.groups
        .filter((g) => g.name.trim())
        .map((g) => ({ ...g, name: g.name.trim() }));
      if (editId) {
        await updateSupervisor(editId, payload);
        setMsg('Superviseur mis à jour.');
      } else {
        await createSupervisor(payload);
        setMsg('Superviseur créé.');
      }
      cancel();
      load();
    } catch (err) {
      // Messages DRF ({name: [...], groups: [...]}) a plat, lisibles (ex. nom deja pris).
      let m = 'Enregistrement impossible.';
      try {
        const p = JSON.parse(err.message);
        const flat = (v) => (Array.isArray(v) ? v.flatMap(flat) : v && typeof v === 'object' ? Object.values(v).flatMap(flat) : [String(v)]);
        if (p) m += ' ' + flat(p).join(' ');
      } catch (_) { /* noop */ }
      setError(m);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Supprimer le superviseur "${s.name}" ?`)) return;
    setBusy(true);
    try { await deleteSupervisor(s.id); load(); } catch (err) { setError('Suppression impossible.'); }
    finally { setBusy(false); }
  };

  return (
    <Layout>
      <p className="sv-breadcrumb">
        Ruta: <Link to="/dashboard" className="bc-link">Menu</Link>
        {' >> '}
        <Link to="/parametres" className="bc-link">Parametres</Link>
        {' >> '}
        <span className="bc-current">Superviseurs</span>
      </p>

      {error && <p className="sv-error">{error}</p>}
      {msg && <p className="sv-ok">{msg}</p>}

      <table className="sv-table">
        <thead>
          <tr><th>N°</th><th>Nom</th><th>Username</th><th>Groups</th><th>Banques extra</th><th>Actif</th><th>Actions</th></tr>
        </thead>
        <tbody>
          {supervisors.length === 0 && (
            <tr><td colSpan="7" className="sv-empty">Aucun superviseur.</td></tr>
          )}
          {supervisors.map((s) => (
            <tr key={s.id}>
              <td>{s.seq ?? '—'}</td>
              <td>{s.display_name || s.name}</td>
              <td>{s.username}</td>
              <td>{(s.group_names || []).join(', ') || '—'}</td>
              <td>{(s.bank_names || []).join(', ') || '—'}</td>
              <td>{s.is_active ? 'Oui' : 'Non'}</td>
              <td className="sv-actions">
                <button type="button" onClick={() => startEdit(s)}>Modifier</button>
                <button type="button" className="sv-del" onClick={() => remove(s)} disabled={busy}>Supprimer</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <h3 className="sv-subtitle">Groups</h3>
      <table className="sv-table">
        <thead>
          <tr><th>N°</th><th>Group</th><th>Superviseur</th><th>Adresse</th><th>Banques</th></tr>
        </thead>
        <tbody>
          {groups.length === 0 && (
            <tr><td colSpan="5" className="sv-empty">Aucun group.</td></tr>
          )}
          {groups.map((g) => {
            const inGroup = banks.filter((b) => b.group === g.id);
            return (
              <tr key={g.id}>
                <td>{g.seq ?? '—'}</td>
                <td>{g.display_name || g.name}</td>
                <td>{g.supervisor_name || <em className="sv-hint">sans superviseur</em>}</td>
                <td>{g.address || '—'}</td>
                <td>{inGroup.length ? `${inGroup.length} : ${inGroup.map(bankLabel).join(', ')}` : '—'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <p className="sv-hint">
        Banques sans group ({freeBanks.length}) : {freeBanks.length ? freeBanks.map(bankLabel).join(', ') : 'aucune'}.
        {' '}Supprimer un group ne supprime jamais ses banques : elles deviennent libres et peuvent être remises
        dans un autre group (formulaire ci-dessous, ou page Banque).
      </p>

      <form className="sv-form" onSubmit={submit}>
        <h3>{editId ? 'Modifier le superviseur' : 'Nouveau superviseur'}</h3>
        <div className="sv-row">
          <label>Nom</label>
          <input value={form.name} onChange={(e) => set('name', e.target.value)} required />
        </div>
        <div className="sv-row">
          <label>Username</label>
          <input value={form.username} onChange={(e) => set('username', e.target.value)} required />
        </div>
        <div className="sv-row">
          <label>Mot de passe</label>
          <input
            type="password"
            value={form.password}
            onChange={(e) => set('password', e.target.value)}
            placeholder={editId ? 'laisser vide = inchangé' : ''}
            required={!editId}
          />
        </div>

        <div className="sv-row sv-groups">
          <label>Groups gérés</label>
          <div className="sv-group-list">
            {form.groups.length === 0 && <p className="sv-hint">Aucun group. Un group = un paquet de banques nommé, géré par ce superviseur.</p>}
            {form.groups.map((g, idx) => {
              const taken = groupedBankIds(idx);
              return (
                <div key={g.id ?? `new-${idx}`} className="sv-group-card">
                  <div className="sv-group-head">
                    <input
                      className="sv-group-name"
                      placeholder="Nom du group (ex: PIPO)"
                      value={g.name}
                      onChange={(e) => setGroupName(idx, e.target.value)}
                    />
                    <input
                      className="sv-group-address"
                      placeholder="Adresse du group"
                      value={g.address}
                      onChange={(e) => setGroupAddress(idx, e.target.value)}
                    />
                    <button type="button" className="sv-del" onClick={() => removeGroup(idx)}>Retirer</button>
                  </div>
                  {/* Adresse du group imprimée sur les tickets de TOUTES ses banques
                      (au lieu de celle de la company) — cf. BankLoginView. */}
                  <label className="sv-group-print-address">
                    <input
                      type="checkbox"
                      checked={!!g.is_print_adresse}
                      onChange={(e) => setGroupPrintAddress(idx, e.target.checked)}
                    />
                    Imprimer l'adresse du group sur les tickets (au lieu de celle de la company)
                  </label>
                  {g.is_print_adresse && !g.address.trim() && (
                    <div className="sv-group-print-warning">
                      Adresse du group vide : l'adresse de la company restera imprimée.
                    </div>
                  )}
                  <div className="sv-bank-list">
                    {banks.map((b) => (
                      <label key={b.id} className={`sv-bank-item ${taken.has(b.id) ? 'sv-bank-disabled' : ''}`}>
                        <input
                          type="checkbox"
                          checked={g.bank_ids.includes(b.id)}
                          disabled={taken.has(b.id)}
                          onChange={() => toggleGroupBank(idx, b.id)}
                        />
                        {b.seq != null ? `${b.seq} - ` : ''}{b.name} {b.username ? `(${b.username})` : ''}
                        {otherGroupName(b) && !g.bank_ids.includes(b.id) && !taken.has(b.id) && (
                          <em className="sv-hint"> — actuellement dans {otherGroupName(b)} (sera déplacée)</em>
                        )}
                      </label>
                    ))}
                  </div>
                </div>
              );
            })}
            <button type="button" className="sv-add-group" onClick={addGroup}>+ Nouveau group</button>
          </div>
        </div>

        <div className="sv-row sv-banks">
          <label>Banques en extra <span className="sv-hint">(hors group)</span></label>
          <div className="sv-bank-list">
            {banks.map((b) => {
              const inGroup = groupedBankIds(-1).has(b.id);
              const other = inGroup ? null : otherGroupName(b);
              const checked = form.banks.includes(b.id);
              // deja cochee (donnees anciennes) : on laisse pouvoir la DECOCHER
              const disabled = inGroup || (other !== null && !checked);
              return (
                <label key={b.id} className={`sv-bank-item ${disabled ? 'sv-bank-disabled' : ''}`}>
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={disabled}
                    onChange={() => toggleExtraBank(b.id)}
                  />
                  {b.seq != null ? `${b.seq} - ` : ''}{b.name} {b.username ? `(${b.username})` : ''}
                  {inGroup && <em className="sv-hint"> — dans un group de ce superviseur</em>}
                  {other && <em className="sv-hint"> — dans le group {other}{checked ? ' (à décocher)' : ''}</em>}
                </label>
              );
            })}
          </div>
        </div>

        <div className="sv-btns">
          <button type="submit" className="sv-save" disabled={busy}>
            {busy ? '…' : (editId ? 'Enregistrer' : 'Créer')}
          </button>
          {editId && <button type="button" onClick={cancel}>Annuler</button>}
        </div>
      </form>
    </Layout>
  );
};

export default SupervisorsPage;
